import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DollarSign, Download, RefreshCw, Search, X } from 'lucide-react';
import { erpAPI } from '../../services/api';
import SearchableSelect from '../../components/admin/SearchableSelect';
import Toast from '../../components/admin/Toast';
import FinancialDocumentDrawer from '../../components/admin/FinancialDocumentDrawer';
import { exportCSV } from '../../utils/exportUtils';

const money = (v: any) => `\u09F3${Number(v || 0).toLocaleString('en-BD', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (v: any) => v ? new Date(v).toLocaleDateString('en-BD') : '-';
const dt = (v: any) => v ? new Date(v).toLocaleString('en-BD') : '-';
const errorText = (e: any) => e?.response?.data?.detail || e?.message || 'Request failed';

export default function ReceivablesPage({ me, locationFilter }: { me: any; locationFilter: string }) {
  const [data, setData] = useState<any[]>([]);
  const [cashBankAccounts, setCashBankAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [selected, setSelected] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payForm, setPayForm] = useState<Record<string, any>>({});
  const [payments, setPayments] = useState<any[]>([]);
  const [payLoading, setPayLoading] = useState(false);

  const can = useCallback((code: string) => Boolean(me?.is_superuser || me?.is_admin || (me?.permissions || []).includes(code)), [me]);
  const showLoc = useMemo(() => !locationFilter, [locationFilter]);
  const params = useMemo(() => ({ ...filters, ...(locationFilter ? { location: locationFilter } : {}) }), [filters, locationFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [resp, accts] = await Promise.all([
        erpAPI.list('accounts-section/receivables', params),
        erpAPI.list('accounts-section/cash-bank', {}).catch(() => ({ data: { accounts: [] } })),
      ]);
      setData(resp.data);
      setCashBankAccounts(accts.data?.accounts || []);
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    const total = data.reduce((s: number, r: any) => s + Number(r.total_amount || 0), 0);
    const due = data.reduce((s: number, r: any) => s + Number(r.due_amount || 0), 0);
    const overdue = data.filter((r: any) => r.due_amount > 0 && r.due_date && new Date(r.due_date) < new Date());
    return { total, due, overdueAmt: overdue.reduce((s: number, r: any) => s + Number(r.due_amount || 0), 0) };
  }, [data]);

  const handleReceive = async () => {
    if (!payForm.invoice_id || !payForm.amount || !payForm.account) {
      setMessage({ type: 'error', text: 'Invoice, amount, and account are required.' }); return;
    }
    if (Number(payForm.amount) > Number(payForm.remaining_due)) {
      setMessage({ type: 'error', text: 'Amount cannot exceed remaining due.' }); return;
    }
    try {
      await erpAPI.create('accounts-section/receivables/receive', {
        invoice_id: Number(payForm.invoice_id), amount: Number(payForm.amount),
        account: Number(payForm.account), reference: payForm.reference || '',
        payment_date: payForm.payment_date || undefined, notes: payForm.notes || '',
      });
      setMessage({ type: 'success', text: 'Payment received.' });
      setShowPayModal(false); setPayForm({}); setDrawerOpen(false); load();
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
  };

  const openDrawer = async (item: any) => {
    setSelected(item);
    setPayLoading(true);
    try {
      const resp = await erpAPI.list('accounts-section/transactions', {
        reference_type: 'WebsiteOrderReceivable', reference_id: String(item.id),
      });
      setPayments(resp.data?.results || []);
    } catch { setPayments([]); }
    finally { setPayLoading(false); setDrawerOpen(true); }
  };

  const filtered = useMemo(() => {
    let list = [...data];
    if (filters.search) { const q = filters.search.toLowerCase(); list = list.filter(r => (r.invoice_no || '').toLowerCase().includes(q) || (r.party || '').toLowerCase().includes(q)); }
    if (filters.status) list = list.filter(r => r.status === filters.status);
    if (filters.type) list = list.filter(r => r.type === filters.type);
    return list;
  }, [data, filters]);

  return <div className="erp-page">
    <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={() => setMessage(null)} />

    <div className="acct-kpi-grid">
      <div className="erp-stat"><span>Total Receivable</span><strong style={{ color: '#059669' }}>{money(summary.total)}</strong></div>
      <div className="erp-stat"><span>Overdue</span><strong style={{ color: '#dc2626' }}>{money(summary.overdueAmt)}</strong></div>
      <div className="erp-stat"><span>Pending</span><strong style={{ color: '#d97706' }}>{money(summary.due)}</strong></div>
    </div>

    <div className="erp-filterbar" style={{ flexWrap: 'wrap' }}>
      <Search size={15} style={{ color: '#94a3b8' }} />
      <input value={filters.search || ''} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
        placeholder="Search invoice, party..." style={{ border: 0, background: 'transparent', flex: 1, minWidth: 160, outline: 'none' }} />
      <label>Type</label>
      <SearchableSelect value={filters.type || ''} onChange={n => setFilters(f => ({ ...f, type: n }))}
        placeholder="All" options={['Website order', 'Internal transfer'].map(t => ({ value: t, label: t }))} />
      <label>Status</label>
      <SearchableSelect value={filters.status || ''} onChange={n => setFilters(f => ({ ...f, status: n }))}
        placeholder="All" options={['UNPAID', 'PARTIAL', 'PAID'].map(s => ({ value: s, label: s }))} />
      {Object.keys(filters).some(k => filters[k]) && (
        <button onClick={() => setFilters({})} style={{ border: 0, background: '#fee2e2', color: '#991b1b', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontWeight: 600, fontSize: '.7rem' }}>
          <X size={12} /> Clear
        </button>
      )}
      <button onClick={() => exportCSV(filtered, 'receivables')} className="erp-btn secondary" style={{ padding: '5px 8px', fontSize: '.7rem', marginLeft: 'auto' }}>
        <Download size={12} /> CSV
      </button>
    </div>

    {loading ? <div className="erp-loading"><RefreshCw className="spin" /> Loading...</div> :
      <section className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="erp-table-wrap" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
          <table style={{ position: 'relative' }}>
            <thead><tr style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff' }}>
              <th>Invoice</th>{showLoc && <th>Location</th>}<th>Party</th><th>Type</th><th>Total</th><th>Received</th><th>Due</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>{filtered.length === 0 ? <tr><td className="erp-empty" colSpan={showLoc ? 9 : 8}>No receivables found.</td></tr> :
              filtered.map((r: any, i: number) => <tr key={r.id || i}
                onClick={() => openDrawer(r)} style={{ cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ fontWeight: 600 }}>{r.invoice_no || '-'}</td>
                {showLoc && <td><span style={{ color: '#4f46e5', fontWeight: 600, fontSize: '.75rem' }}>{r.location_name || '-'}</span></td>}
                <td>{r.party || '-'}</td>
                <td><span className="erp-status" style={{ background: r.type === 'Internal transfer' ? '#eff6ff' : '#f0fdf4', color: r.type === 'Internal transfer' ? '#1d4ed8' : '#166534' }}>{r.type || '-'}</span></td>
                <td style={{ fontWeight: 600 }}>{money(r.total_amount)}</td>
                <td style={{ color: '#059669' }}>{money(r.received_amount)}</td>
                <td style={{ fontWeight: 700, color: Number(r.due_amount) > 0 ? '#dc2626' : '#059669' }}>{money(r.due_amount)}</td>
                <td><span className={`erp-status ${r.status === 'PAID' ? 'received' : r.status === 'PARTIAL' ? 'dispatched' : ''}`}>{r.status || '-'}</span></td>
                <td style={{ textAlign: 'right' }}>
                  {can('accounts.receivable.receive') && Number(r.due_amount) > 0 && (
                    <button className="erp-btn" style={{ padding: '4px 10px', fontSize: '.72rem' }}
                      onClick={(e) => { e.stopPropagation(); setPayForm({ invoice_id: r.id || r.invoice_no, remaining_due: r.due_amount, amount: r.due_amount, account: '', reference: '', payment_date: new Date().toISOString().slice(0, 10), notes: '' }); setSelected(r); setShowPayModal(true); }}>
                      <DollarSign size={12} /> Receive
                    </button>
                  )}
                </td>
              </tr>)}</tbody>
          </table>
        </div>
      </section>}

    {showPayModal && selected && <div className="acct-modal-overlay" onClick={() => setShowPayModal(false)}>
      <div className="acct-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="acct-modal-header"><h3 className="acct-modal-title">Receive Payment</h3>
          <button className="acct-modal-close" onClick={() => setShowPayModal(false)}>✕</button></div>
        <div className="acct-modal-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="acct-form-row">
              <div className="acct-form-group"><label className="acct-form-label">Invoice</label><input className="acct-form-input" value={selected.invoice_no || '-'} readOnly /></div>
              <div className="acct-form-group"><label className="acct-form-label">Remaining Due</label><input className="acct-form-input" value={money(selected.due_amount)} readOnly style={{ fontWeight: 700, color: '#dc2626' }} /></div>
            </div>
            <div className="acct-form-row">
              <div className="acct-form-group"><label className="acct-form-label">Amount *</label>
                <input className="acct-form-input" type="number" value={payForm.amount || ''} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} max={selected.due_amount} /></div>
              <div className="acct-form-group"><label className="acct-form-label">Date</label>
                <input className="acct-form-input" type="date" value={payForm.payment_date || ''} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))} /></div>
            </div>
            <div className="acct-form-group"><label className="acct-form-label">Deposit To *</label>
              <SearchableSelect value={payForm.account || ''} onChange={n => setPayForm(f => ({ ...f, account: n }))}
                placeholder="Select account" options={cashBankAccounts.map((x: any) => ({ value: x.id, label: `${x.account || x.name} - ${x.location_name}` }))} /></div>
            <div className="acct-form-row">
              <div className="acct-form-group"><label className="acct-form-label">Reference</label>
                <input className="acct-form-input" value={payForm.reference || ''} onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))} placeholder="Optional" /></div>
              <div className="acct-form-group"><label className="acct-form-label">Notes</label>
                <input className="acct-form-input" value={payForm.notes || ''} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" /></div>
            </div>
            <div className="acct-form-actions">
              <button className="acct-btn acct-btn-secondary" onClick={() => setShowPayModal(false)}>Cancel</button>
              <button className="acct-btn acct-btn-primary" onClick={handleReceive}><DollarSign size={15} /> Record Payment</button>
            </div>
          </div>
        </div>
      </div>
    </div>}

    <FinancialDocumentDrawer
      open={drawerOpen} onClose={() => setDrawerOpen(false)}
      documentType="receivable" title={selected?.invoice_no || 'Receivable'}
      subtitle={`${selected?.type || ''} — ${selected?.party || ''}`}
      fields={[
        { label: 'Total', value: <span style={{ fontWeight: 700 }}>{money(selected?.total_amount)}</span> },
        { label: 'Received', value: <span style={{ color: '#059669', fontWeight: 600 }}>{money(selected?.received_amount)}</span> },
        { label: 'Due', value: <span style={{ color: '#dc2626', fontWeight: 600 }}>{money(selected?.due_amount)}</span> },
        { label: 'Business Location', value: <span style={{ color: '#4f46e5', fontWeight: 600 }}>{selected?.location_name || '-'}</span> },
        { label: 'Type', value: selected?.type || '-' }, { label: 'Party', value: selected?.party || '-' },
        { label: 'Due Date', value: selected?.due_date ? date(selected.due_date) : '-' },
        { label: 'Created', value: selected?.date ? dt(selected.date) : '-' },
      ]}
      timeline={(() => { const s = selected?.status; return [
        { label: 'Created', date: selected?.date ? dt(selected.date) : undefined, active: true },
        { label: 'Collection Started', active: s === 'PARTIAL' || s === 'PAID' },
        { label: s === 'PARTIAL' ? 'Partially Paid' : 'Fully Paid', active: s === 'PAID' || s === 'PARTIAL' },
        { label: 'Journal Posted', active: s === 'PAID' },
        { label: 'Closed', active: s === 'PAID' },]; })()}
      payments={payments.map((p: any) => ({ date: p.date, method: p.payment_method, account: p.account_name, reference: p.reference, user: p.created_by, amount: p.debit || p.credit || 0 }))}
      paymentsLoading={payLoading}
      actions={[
        can('accounts.receivable.receive') && Number(selected?.due_amount) > 0
          ? { label: 'Receive Payment', icon: <DollarSign size={13} />, onClick: () => { setPayForm({ invoice_id: selected.id || selected.invoice_no, remaining_due: selected.due_amount, amount: selected.due_amount, account: '', reference: '', payment_date: new Date().toISOString().slice(0, 10), notes: '' }); setShowPayModal(true); }, primary: true } : null,
      ].filter(Boolean) as any}
    />
  </div>;
}
